/**
 * Main state management hook for story generation
 * Handles AI integration, static fallback, section-wide suggestions, and autosave
 */

import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import type {
  Character,
  Encounter,
  GeneratorSettings,
  Location,
  LockState,
  Objective,
  SideQuest,
  Skill,
  SlotCounts,
  StorySeedDrafts,
} from "@/types/story.ts";
import { pickMultipleUnique, THEME_CONTENT } from "@/lib/data/themes.ts";
import { getStoryById, type SavedStory, saveStory } from "@/lib/storage/stories.ts";
import {
  buildGenerationPrompt,
  buildSkillUsesPrompt,
  buildStep2Prompt,
  buildStep3Prompt,
  buildStep4ContinuationPrompt,
  buildStep4FieldPrompt,
  buildStoryNamePrompt,
  parseAIResponse,
  parseSkillUsesResponse,
  parseStep2Response,
  parseStep3Response,
  parseStep4FieldResponse,
  parseStoryNameResponse,
} from "@/lib/ai/storyPrompts.ts";
import { parseIncrementalStoryDrafts } from "@/lib/ai/incrementalJsonParser.ts";
import { readLLMStream } from "@/lib/ai/streamReader.ts";
import type { ChatMessage } from "@/lib/ai/types.ts";
import { getCachedResponse, setCachedResponse } from "@/lib/ai/promptCache.ts";
import { loadAllTokens } from "@/lib/auth/tokens.ts";
import { reduceStory, type StoryAction } from "@/hooks/storyReducer.ts";

const SUGGESTION_COUNT = 8;
const STEP2_SKILL_COUNT = 16;
const STEP2_THEME_VARIANT_COUNT = 4;

const buildMessages = (prompt: { system: string; user: string }): ChatMessage[] => [
  { role: "system", content: prompt.system },
  { role: "user", content: prompt.user },
];

const mergeUniqueStrings = (items: string[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.trim();
    if (!key || seen.has(key.toLowerCase())) return false;
    seen.add(key.toLowerCase());
    return true;
  });
};

const mergeUniqueSkills = (items: Skill[]) => {
  const seen = new Set<string>();
  return items.filter((skill) => {
    const key = skill.name.trim();
    if (!key || seen.has(key.toLowerCase())) return false;
    seen.add(key.toLowerCase());
    return true;
  });
};

const mergeUniqueObjectives = (items: Objective[]) => {
  const seen = new Set<string>();
  return items.filter((objective) => {
    const key = objective.description.trim();
    if (!key || seen.has(key.toLowerCase())) return false;
    seen.add(key.toLowerCase());
    return true;
  });
};

const buildStep4ConversationKey = (
  story: SavedStory,
  field: "twist" | "intro" | "sideQuest" | "character" | "location" | "encounter",
): string => {
  const { settings, storySeed } = story;
  const skills = settings.lockedSkills.map((skill) => `${skill.emoji}${skill.name}`);
  return JSON.stringify({
    field,
    language: settings.language,
    ageGroup: settings.ageGroup,
    theme: settings.theme,
    themeVariant: settings.themeVariant,
    additionalInstructions: settings.additionalInstructions,
    lockedSkills: skills,
    mainObjective: storySeed.mainObjective.description,
  });
};

const appendFieldDrafts = (
  drafts: StorySeedDrafts,
  field: "twist" | "intro" | "sideQuest" | "character" | "location" | "encounter",
  items: Character[] | Location[] | SideQuest[] | string[] | Encounter[],
): Partial<StorySeedDrafts> => {
  const draftField = field === "twist"
    ? "storyTwist"
    : field === "intro"
    ? "storyIntro"
    : field === "sideQuest"
    ? "sideQuests"
    : field === "character"
    ? "characters"
    : field === "location"
    ? "locations"
    : "encounters";

  const existing = drafts[draftField].options;

  if (field === "twist" || field === "intro") {
    const merged = mergeUniqueStrings([...(existing as string[]), ...(items as string[])]);
    return { [draftField]: { options: merged } } as Partial<StorySeedDrafts>;
  }

  if (field === "sideQuest") {
    const seen = new Set<string>();
    const merged = [...(existing as SideQuest[]), ...(items as SideQuest[])].filter((quest) => {
      const key = quest.description.trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return { [draftField]: { options: merged } } as Partial<StorySeedDrafts>;
  }

  if (field === "encounter") {
    const seen = new Set<string>();
    const merged = [...(existing as Encounter[]), ...(items as Encounter[])].filter((encounter) => {
      const key = encounter.description.trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return { [draftField]: { options: merged } } as Partial<StorySeedDrafts>;
  }

  if (field === "character") {
    const seen = new Set<string>();
    const merged = [...(existing as Character[]), ...(items as Character[])].filter((character) => {
      const key = `${character.name}|${character.role}`.trim().toLowerCase();
      if (!character.name || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return { [draftField]: { options: merged } } as Partial<StorySeedDrafts>;
  }

  const seen = new Set<string>();
  const merged = [...(existing as Location[]), ...(items as Location[])].filter((location) => {
    const key = location.name.trim().toLowerCase();
    if (!location.name || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return { [draftField]: { options: merged } } as Partial<StorySeedDrafts>;
};

const getSelectedModel = (
  provider: string,
): string | undefined => {
  const saved = localStorage.getItem(`ai_model_${provider}`)?.trim();
  return saved ? saved : undefined;
};

const getModelCacheKey = (
  provider: string,
  model: string | undefined,
): string => model || `auto:${provider}`;

interface UseStoryGeneratorReturn {
  story: SavedStory | null;
  generatingFields: Partial<Record<GenerationField, boolean>>;
  generationError: string | null;
  generateContent: () => Promise<void>;
  generateStoryNames: (options?: { forceFresh?: boolean }) => Promise<string[]>;
  clearDrafts: (fields: Array<keyof StorySeedDrafts>) => void;
  updateDraftOptions: (
    field: keyof StorySeedDrafts,
    options: StorySeedDrafts[keyof StorySeedDrafts]["options"],
  ) => void;
  generateStep2: (
    options?: {
      mode?: "replace" | "append";
      keepSkills?: Skill[];
      keepThemeVariants?: string[];
      forceFresh?: boolean;
    },
  ) => Promise<void>;
  generateStep3: (
    options?: { mode?: "replace" | "append"; keepObjectives?: Objective[]; forceFresh?: boolean },
  ) => Promise<void>;
  generateFieldDraft: (
    field: "twist" | "intro" | "sideQuest" | "character" | "location" | "encounter",
    options?: { mode?: "replace" | "append"; forceFresh?: boolean },
  ) => Promise<Character[] | Location[] | SideQuest[] | Encounter[] | string[]>;
  generateStep4Bulk: (options?: { forceFresh?: boolean }) => Promise<void>;
  preloadStep2: () => Promise<void>;
  preloadStep3: (skills: Skill[], themeVariant: string) => Promise<void>;
  preloadStep4Bulk: (objective: Objective) => Promise<void>;
  selectSuggestion: (
    field: keyof StorySeedDrafts,
    item: Character | Location | Skill | SideQuest | Objective | Encounter | string,
  ) => void;
  toggleLock: (field: keyof LockState, arrayIndex?: number) => void;
  removeSuggestion: (
    field: keyof StorySeedDrafts,
    item: Character | Location | Skill | SideQuest | Objective | Encounter | string,
  ) => void;
  lockAll: () => void;
  unlockAll: () => void;
  updateSettings: (settings: Partial<GeneratorSettings>) => void;
  updateStoryName: (name: string) => void;
  updateStoryIntro: (intro: string) => void;
  updateSlotCount: (field: keyof SlotCounts, delta: number) => void;
  updateSideQuest: (slotIndex: number, sideQuest: Partial<SideQuest>) => void;
  updateCharacter: (slotIndex: number, character: Partial<Character>) => void;
  updateLocation: (slotIndex: number, location: Partial<Location>) => void;
  updateEncounter: (slotIndex: number, encounter: Partial<Encounter>) => void;
  updateSkill: (slotIndex: number, skill: Partial<Skill>) => void;
  updateMainObjective: (objective: Partial<Objective>) => void;
  updateStoryTwist: (storyTwist: string) => void;
  setStep: (step: 1 | 2 | 3 | 4) => void;
  lockInStep2: (skills: Skill[], themeVariant: string) => void;
  lockInStep3: (objective: Objective) => void;
  generateSkillUses: (
    itemType: "objective" | "sideQuest" | "character" | "location" | "encounter",
    itemIndex?: number,
    options?: { forceFresh?: boolean; targetDescription?: string },
  ) => Promise<string[]>;
  loadTestData: () => void;
}

type GenerationField =
  | "content"
  | "storyName"
  | "step2"
  | "step3"
  | "intro"
  | "twist"
  | "sideQuest"
  | "character"
  | "location"
  | "encounter"
  | "skillUses";

export function useStoryGenerator(storyId: string): UseStoryGeneratorReturn {
  const [story, dispatch] = useReducer(reduceStory, null as SavedStory | null);
  const [generatingFields, setGeneratingFields] = useState<
    Partial<Record<GenerationField, boolean>>
  >({});
  const [generationError, setGenerationError] = useState<string | null>(null);
  const saveTimeoutRef = useRef<number | null>(null);
  const lastActionRef = useRef<StoryAction["type"] | null>(null);
  const abortControllersRef = useRef<Partial<Record<GenerationField, AbortController>>>({});
  const requestIdRef = useRef<Partial<Record<GenerationField, number>>>({});
  const step4ConversationRef = useRef<{
    key: string | null;
    field: "twist" | "intro" | "sideQuest" | "character" | "location" | "encounter" | null;
    messages: ChatMessage[];
  }>({ key: null, field: null, messages: [] });
  const promptInFlightRef = useRef(new Map<string, Promise<string>>());

  const resetStep4Conversation = useCallback(() => {
    step4ConversationRef.current = { key: null, field: null, messages: [] };
  }, []);

  const dispatchStory = useCallback((action: StoryAction) => {
    lastActionRef.current = action.type;
    dispatch(action);
  }, []);

  const setFieldGenerating = useCallback((field: GenerationField, value: boolean) => {
    setGeneratingFields((prev) => {
      if (prev[field] === value) return prev;
      return { ...prev, [field]: value };
    });
  }, []);

  const createAbortController = useCallback((field: GenerationField) => {
    abortControllersRef.current[field]?.abort();
    const controller = new AbortController();
    abortControllersRef.current[field] = controller;
    return controller;
  }, []);

  const nextRequestId = useCallback((field: GenerationField) => {
    const next = (requestIdRef.current[field] ?? 0) + 1;
    requestIdRef.current[field] = next;
    return next;
  }, []);

  const getPromptInFlightKey = useCallback((
    model: string,
    prompt: { system: string; user: string },
  ) => {
    return `${model}::${prompt.system}::${prompt.user}`;
  }, []);

  const preloadPromptToCache = useCallback(async (prompt: {
    system: string;
    user: string;
    maxTokens: number;
  }) => {
    const tokens = loadAllTokens();
    const hasAnyToken = tokens["default"] || tokens.copilot || tokens.antigravity ||
      tokens.openrouter || tokens.gemini || tokens.groq || tokens.perplexity;
    if (!hasAnyToken) return;

    const provider = tokens.activeProvider ||
      (tokens["default"]
        ? "default"
        : tokens.copilot
        ? "copilot"
        : tokens.antigravity
        ? "antigravity"
        : tokens.openrouter
        ? "openrouter"
        : tokens.gemini
        ? "gemini"
        : tokens.groq
        ? "groq"
        : tokens.perplexity
        ? "perplexity"
        : null);

    if (!provider) return;

    const activeToken = tokens[provider];
    if (!activeToken) return;

    const selectedModel = getSelectedModel(provider);
    const cacheModel = getModelCacheKey(provider, selectedModel);

    if (getCachedResponse(prompt.system, prompt.user, cacheModel)) {
      return;
    }

    const inflightKey = getPromptInFlightKey(cacheModel, prompt);
    const existingRequest = promptInFlightRef.current.get(inflightKey);
    if (existingRequest) {
      await existingRequest;
      return;
    }

    const requestPromise = (async () => {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          provider,
          model: selectedModel,
          messages: buildMessages(prompt),
          accessToken: activeToken.accessToken,
          max_tokens: prompt.maxTokens,
        }),
      });

      if (!response.ok) {
        throw new Error(`AI request failed: ${response.statusText}`);
      }

      const fullText = await readLLMStream(response, () => {});
      setCachedResponse(prompt.system, prompt.user, cacheModel, fullText);
      return fullText;
    })();

    promptInFlightRef.current.set(inflightKey, requestPromise);

    try {
      await requestPromise;
    } catch (error) {
      console.warn("Prompt preload failed:", error);
    } finally {
      if (promptInFlightRef.current.get(inflightKey) === requestPromise) {
        promptInFlightRef.current.delete(inflightKey);
      }
    }
  }, [getPromptInFlightKey]);

  // Load story on mount
  useEffect(() => {
    let cancelled = false;

    const loadStory = async () => {
      const loaded = await getStoryById(storyId);
      if (!cancelled) {
        dispatchStory({ type: "load", story: loaded });
      }
    };

    void loadStory();

    return () => {
      cancelled = true;
    };
  }, [storyId, dispatchStory]);

  // Autosave with debounce
  const triggerAutosave = useCallback((updatedStory: SavedStory) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      void saveStory(updatedStory).catch((error) => {
        console.error("[Autosave] Failed to save story:", error);
      });
    }, 1000);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      Object.values(abortControllersRef.current).forEach((controller) => controller?.abort());
    };
  }, []);

  useEffect(() => {
    if (!story) return;
    if (lastActionRef.current === "load") {
      lastActionRef.current = null;
      return;
    }
    triggerAutosave(story);
  }, [story, triggerAutosave]);

  // Select suggestion - auto-fills first empty slot and removes from options
  const selectSuggestion = useCallback(
    (
      field: keyof StorySeedDrafts,
      item: Character | Location | Skill | SideQuest | Objective | Encounter | string,
    ) => {
      dispatchStory({ type: "selectSuggestion", field, item });
    },
    [dispatchStory],
  );

  const removeSuggestion = useCallback(
    (
      field: keyof StorySeedDrafts,
      item: Character | Location | Skill | SideQuest | Objective | Encounter | string,
    ) => {
      dispatchStory({ type: "removeSuggestion", field, item });
    },
    [dispatchStory],
  );

  const clearDrafts = useCallback(
    (fields: Array<keyof StorySeedDrafts>) => {
      dispatchStory({ type: "clearDrafts", fields });
    },
    [dispatchStory],
  );

  const updateDraftOptions = useCallback(
    (
      field: keyof StorySeedDrafts,
      options: StorySeedDrafts[keyof StorySeedDrafts]["options"],
    ) => {
      dispatchStory({
        type: "setPartialDrafts",
        drafts: { [field]: { options } } as Partial<StorySeedDrafts>,
      });
    },
    [dispatchStory],
  );

  // Generate content
  const generateContent = useCallback(async () => {
    if (!story) return;

    const field: GenerationField = "content";
    const controller = createAbortController(field);
    const requestId = nextRequestId(field);

    setFieldGenerating(field, true);
    setGenerationError(null);

    try {
      const tokens = loadAllTokens();
      const hasAnyToken = tokens["default"] || tokens.copilot || tokens.antigravity ||
        tokens.openrouter || tokens.gemini || tokens.groq || tokens.perplexity;

      let generatedDrafts: StorySeedDrafts;

      if (hasAnyToken) {
        // Try AI generation
        const prompt = buildGenerationPrompt(
          story.storySeed,
          story.lockState,
          story.settings,
          SUGGESTION_COUNT,
        );

        // Determine which provider to use
        const provider = tokens.activeProvider ||
          (tokens["default"]
            ? "default"
            : tokens.copilot
            ? "copilot"
            : tokens.antigravity
            ? "antigravity"
            : tokens.openrouter
            ? "openrouter"
            : tokens.gemini
            ? "gemini"
            : tokens.groq
            ? "groq"
            : tokens.perplexity
            ? "perplexity"
            : null);

        if (!provider) {
          throw new Error("No active provider found");
        }

        const activeToken = tokens[provider];
        if (!activeToken) {
          throw new Error(`No token found for provider: ${provider}`);
        }

        const selectedModel = getSelectedModel(provider);
        const cacheModel = getModelCacheKey(provider, selectedModel);

        const cached = getCachedResponse(prompt.system, prompt.user, cacheModel);

        if (cached) {
          generatedDrafts = parseAIResponse(cached, story.settings.slotCounts);
        } else {
          const response = await fetch("/api/chat", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            signal: controller.signal,
            body: JSON.stringify({
              provider,
              model: selectedModel,
              messages: buildMessages(prompt),
              accessToken: activeToken.accessToken,
              max_tokens: prompt.maxTokens,
            }),
          });

          if (!response.ok) {
            let message = `AI request failed: ${response.statusText}`;
            try {
              const errorData = await response.json();
              message = errorData.error || message;
            } catch {
              try {
                const text = await response.text();
                if (text) message = text;
              } catch {
                // Ignore
              }
            }
            throw new Error(message);
          }

          const fullText = await readLLMStream(response, (partialText) => {
            if (controller.signal.aborted) return;
            const { drafts } = parseIncrementalStoryDrafts(partialText);
            if (Object.keys(drafts).length > 0) {
              dispatchStory({ type: "setPartialDrafts", drafts });
            }
          });

          setCachedResponse(prompt.system, prompt.user, cacheModel, fullText);
          generatedDrafts = parseAIResponse(fullText, story.settings.slotCounts);
        }
      } else {
        // Fallback to static theme
        const theme = THEME_CONTENT[story.settings.theme] || THEME_CONTENT["Talking Animal Tales"];

        generatedDrafts = {
          storyName: { options: pickMultipleUnique(theme.storyNames, SUGGESTION_COUNT) },
          mainObjective: { options: pickMultipleUnique(theme.objectives, SUGGESTION_COUNT) },
          storyTwist: { options: pickMultipleUnique(theme.storyTwists, SUGGESTION_COUNT) },
          storyIntro: { options: [] },
          themeVariant: { options: [] },
          skills: { options: pickMultipleUnique(theme.skills, SUGGESTION_COUNT) },
          characters: { options: pickMultipleUnique(theme.characters, SUGGESTION_COUNT) },
          locations: { options: pickMultipleUnique(theme.locations, SUGGESTION_COUNT) },
          sideQuests: { options: pickMultipleUnique(theme.sideQuests, SUGGESTION_COUNT) },
          encounters: { options: [] },
        };

        setGenerationError("Using fallback themes (no AI provider connected)");
      }

      // Merge with existing locks - only update unlocked fields
      const newDrafts: StorySeedDrafts = {
        storyName: story.lockState.storyName ? story.drafts.storyName : generatedDrafts.storyName,
        mainObjective: story.lockState.mainObjective
          ? story.drafts.mainObjective
          : generatedDrafts.mainObjective,
        storyTwist: story.lockState.storyTwist
          ? story.drafts.storyTwist
          : generatedDrafts.storyTwist,
        storyIntro: story.lockState.storyIntro
          ? story.drafts.storyIntro
          : generatedDrafts.storyIntro,
        themeVariant: generatedDrafts.themeVariant || { options: [] },
        skills: generatedDrafts.skills,
        characters: generatedDrafts.characters,
        locations: generatedDrafts.locations,
        sideQuests: generatedDrafts.sideQuests,
        encounters: generatedDrafts.encounters,
      };

      // Don't auto-fill - leave slots empty and only show suggestions
      // Users must explicitly select from suggestions

      if (requestIdRef.current[field] === requestId) {
        dispatchStory({ type: "setDrafts", drafts: newDrafts });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      console.error("Generation error:", error);
      if (requestIdRef.current[field] === requestId) {
        setGenerationError(error instanceof Error ? error.message : "Failed to generate content");
      }
    } finally {
      if (requestIdRef.current[field] === requestId) {
        setFieldGenerating(field, false);
        if (abortControllersRef.current[field] === controller) {
          delete abortControllersRef.current[field];
        }
      }
    }
  }, [story, dispatchStory]);

  const generateStoryNames = useCallback(async (options?: { forceFresh?: boolean }) => {
    if (!story) return [];

    const field: GenerationField = "storyName";
    const controller = createAbortController(field);
    const requestId = nextRequestId(field);

    setFieldGenerating(field, true);
    setGenerationError(null);

    try {
      const tokens = loadAllTokens();
      const hasAnyToken = tokens["default"] || tokens.copilot || tokens.antigravity ||
        tokens.openrouter || tokens.gemini || tokens.groq || tokens.perplexity;

      if (!hasAnyToken) {
        setGenerationError("No AI provider connected");
        return [];
      }

      const provider = tokens.activeProvider ||
        (tokens["default"]
          ? "default"
          : tokens.copilot
          ? "copilot"
          : tokens.antigravity
          ? "antigravity"
          : tokens.openrouter
          ? "openrouter"
          : tokens.gemini
          ? "gemini"
          : tokens.groq
          ? "groq"
          : tokens.perplexity
          ? "perplexity"
          : null);

      if (!provider) throw new Error("No active provider found");

      const activeToken = tokens[provider];
      if (!activeToken) throw new Error(`No token found for provider: ${provider}`);

      const selectedModel = getSelectedModel(provider);
      const cacheModel = getModelCacheKey(provider, selectedModel);

      const prompt = buildStoryNamePrompt(
        story.settings,
        story.storySeed.mainObjective,
        SUGGESTION_COUNT,
      );

      const cached = options?.forceFresh
        ? null
        : getCachedResponse(prompt.system, prompt.user, cacheModel);

      let storyNames: string[];
      if (cached) {
        storyNames = parseStoryNameResponse(cached);
      } else {
        const response = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          signal: controller.signal,
          body: JSON.stringify({
            provider,
            model: selectedModel,
            messages: buildMessages(prompt),
            accessToken: activeToken.accessToken,
            max_tokens: prompt.maxTokens,
          }),
        });

        if (!response.ok) {
          let message = `AI request failed: ${response.statusText}`;
          try {
            const errorData = await response.json();
            message = errorData.error || message;
          } catch {
            // Ignore
          }
          throw new Error(message);
        }

        const fullText = await readLLMStream(response, (partialText) => {
          if (controller.signal.aborted) return;
          const { drafts } = parseIncrementalStoryDrafts(partialText);
          if (drafts.storyName) {
            dispatchStory({ type: "setPartialDrafts", drafts });
          }
        });

        setCachedResponse(prompt.system, prompt.user, cacheModel, fullText);
        storyNames = parseStoryNameResponse(fullText);
      }

      if (requestIdRef.current[field] === requestId) {
        dispatchStory({
          type: "setPartialDrafts",
          drafts: { storyName: { options: storyNames } },
        });
      }
      return storyNames;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return [];
      }
      console.error("Story name generation error:", error);
      if (requestIdRef.current[field] === requestId) {
        setGenerationError(
          error instanceof Error ? error.message : "Failed to generate story names",
        );
      }
      return [];
    } finally {
      if (requestIdRef.current[field] === requestId) {
        setFieldGenerating(field, false);
        if (abortControllersRef.current[field] === controller) {
          delete abortControllersRef.current[field];
        }
      }
    }
  }, [story, dispatchStory]);

  // Toggle lock
  const toggleLock = useCallback(
    (field: keyof LockState, arrayIndex?: number) => {
      dispatchStory({ type: "toggleLock", field, arrayIndex });
    },
    [dispatchStory],
  );

  // Lock all
  const lockAll = useCallback(() => {
    dispatchStory({ type: "lockAll" });
  }, [dispatchStory]);

  // Unlock all
  const unlockAll = useCallback(() => {
    dispatchStory({ type: "unlockAll" });
  }, [dispatchStory]);

  // Update settings
  const updateSettings = useCallback(
    (settings: Partial<GeneratorSettings>) => {
      dispatchStory({ type: "updateSettings", settings });

      const invalidatesGeneratedContent = settings.language !== undefined ||
        settings.ageGroup !== undefined ||
        settings.theme !== undefined ||
        settings.themeVariant !== undefined ||
        settings.additionalInstructions !== undefined ||
        settings.lockedSkills !== undefined;

      if (invalidatesGeneratedContent) {
        dispatchStory({
          type: "clearDrafts",
          fields: [
            "storyName",
            "mainObjective",
            "storyTwist",
            "storyIntro",
            "characters",
            "locations",
            "sideQuests",
            "encounters",
          ],
        });
        resetStep4Conversation();

        if (settings.lockedSkills !== undefined) {
          dispatchStory({ type: "clearAllSkillUses" });
        }
      }
    },
    [dispatchStory, resetStep4Conversation],
  );

  // Update story name
  const updateStoryName = useCallback(
    (name: string) => {
      dispatchStory({ type: "updateStoryName", name });
    },
    [dispatchStory],
  );

  const updateStoryIntro = useCallback(
    (intro: string) => {
      dispatchStory({ type: "updateStoryIntro", storyIntro: intro });
      resetStep4Conversation();
    },
    [dispatchStory, resetStep4Conversation],
  );

  // Update slot count
  const updateSlotCount = useCallback(
    (field: keyof SlotCounts, delta: number) => {
      dispatchStory({ type: "updateSlotCount", field, delta });
    },
    [dispatchStory],
  );

  // Update individual items
  const updateSideQuest = useCallback(
    (slotIndex: number, quest: Partial<SideQuest>) => {
      dispatchStory({ type: "updateSideQuest", slotIndex, sideQuest: quest });
      resetStep4Conversation();
    },
    [dispatchStory, resetStep4Conversation],
  );

  const updateCharacter = useCallback(
    (slotIndex: number, character: Partial<Character>) => {
      dispatchStory({ type: "updateCharacter", slotIndex, character });
      resetStep4Conversation();
    },
    [dispatchStory, resetStep4Conversation],
  );

  const updateLocation = useCallback(
    (slotIndex: number, location: Partial<Location>) => {
      dispatchStory({ type: "updateLocation", slotIndex, location });
      resetStep4Conversation();
    },
    [dispatchStory, resetStep4Conversation],
  );

  const updateEncounter = useCallback(
    (slotIndex: number, encounter: Partial<Encounter>) => {
      dispatchStory({ type: "updateEncounter", slotIndex, encounter });
    },
    [dispatchStory],
  );

  const updateSkill = useCallback(
    (slotIndex: number, skill: Partial<Skill>) => {
      dispatchStory({ type: "updateSkill", slotIndex, skill });
    },
    [dispatchStory],
  );

  const updateMainObjective = useCallback(
    (objective: Partial<Objective>) => {
      dispatchStory({ type: "updateMainObjective", objective });
      if (objective.description !== undefined) {
        resetStep4Conversation();
      }
    },
    [dispatchStory, resetStep4Conversation],
  );

  const updateStoryTwist = useCallback(
    (storyTwist: string) => {
      dispatchStory({ type: "updateStoryTwist", storyTwist });
      resetStep4Conversation();
    },
    [dispatchStory, resetStep4Conversation],
  );

  // Load test data for UI development
  const loadTestData = useCallback(() => {
    dispatchStory({ type: "loadTestData" });
  }, [dispatchStory]);

  // Step control methods
  const setStep = useCallback(
    (step: 1 | 2 | 3 | 4) => {
      dispatchStory({ type: "setStep", step });
    },
    [dispatchStory],
  );

  const lockInStep2 = useCallback(
    (skills: Skill[], themeVariant: string) => {
      dispatchStory({ type: "lockInStep2", skills, themeVariant });
    },
    [dispatchStory],
  );

  const lockInStep3 = useCallback(
    (objective: Objective) => {
      dispatchStory({ type: "lockInStep3", objective });
    },
    [dispatchStory],
  );

  // Generate Step 2: skills + theme variant
  const generateStep2 = useCallback(async (
    options?: {
      mode?: "replace" | "append";
      keepSkills?: Skill[];
      keepThemeVariants?: string[];
      forceFresh?: boolean;
    },
  ) => {
    if (!story) return;

    const field: GenerationField = "step2";
    const controller = createAbortController(field);
    const requestId = nextRequestId(field);

    setFieldGenerating(field, true);
    setGenerationError(null);

    try {
      const tokens = loadAllTokens();
      const hasAnyToken = tokens["default"] || tokens.copilot || tokens.antigravity ||
        tokens.openrouter || tokens.gemini || tokens.groq || tokens.perplexity;

      if (!hasAnyToken) {
        setGenerationError("No AI provider connected");
        return;
      }

      const prompt = buildStep2Prompt(
        story.settings,
        STEP2_SKILL_COUNT,
        STEP2_THEME_VARIANT_COUNT,
      );

      const provider = tokens.activeProvider ||
        (tokens["default"]
          ? "default"
          : tokens.copilot
          ? "copilot"
          : tokens.antigravity
          ? "antigravity"
          : tokens.openrouter
          ? "openrouter"
          : tokens.gemini
          ? "gemini"
          : tokens.groq
          ? "groq"
          : tokens.perplexity
          ? "perplexity"
          : null);

      if (!provider) throw new Error("No active provider found");

      const activeToken = tokens[provider];
      if (!activeToken) throw new Error(`No token found for provider: ${provider}`);

      const selectedModel = getSelectedModel(provider);
      const cacheModel = getModelCacheKey(provider, selectedModel);

      const baseSkills = options?.keepSkills ?? [];
      const baseThemeVariants = options?.keepThemeVariants ?? [];

      const cached = options?.forceFresh
        ? null
        : getCachedResponse(prompt.system, prompt.user, cacheModel);

      let skills: Skill[];
      let themeVariant: string[];
      const inflightKey = getPromptInFlightKey(cacheModel, prompt);

      if (cached) {
        ({ skills, themeVariant } = parseStep2Response(cached));
      } else {
        const existingRequest = promptInFlightRef.current.get(inflightKey);
        let fullText: string;

        if (existingRequest) {
          fullText = await existingRequest;
        } else {
          const requestPromise = (async () => {
            const response = await fetch("/api/chat", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              signal: controller.signal,
              body: JSON.stringify({
                provider,
                model: selectedModel,
                messages: buildMessages(prompt),
                accessToken: activeToken.accessToken,
                max_tokens: prompt.maxTokens,
              }),
            });

            if (!response.ok) {
              let message = `AI request failed: ${response.statusText}`;
              try {
                const errorData = await response.json();
                message = errorData.error || message;
              } catch {
                // Ignore
              }
              throw new Error(message);
            }

            return await readLLMStream(response, (partialText) => {
              if (controller.signal.aborted) return;
              const { drafts } = parseIncrementalStoryDrafts(partialText);
              const mergedDrafts: Partial<StorySeedDrafts> = {};

              if (drafts.skills?.options) {
                mergedDrafts.skills = {
                  options: mergeUniqueSkills([...baseSkills, ...drafts.skills.options]),
                };
              }

              if (drafts.themeVariant?.options) {
                mergedDrafts.themeVariant = {
                  options: mergeUniqueStrings([
                    ...baseThemeVariants,
                    ...drafts.themeVariant.options,
                  ]),
                };
              }

              if (Object.keys(mergedDrafts).length > 0) {
                dispatchStory({ type: "setPartialDrafts", drafts: mergedDrafts });
              }
            });
          })();

          promptInFlightRef.current.set(inflightKey, requestPromise);
          try {
            fullText = await requestPromise;
          } finally {
            if (promptInFlightRef.current.get(inflightKey) === requestPromise) {
              promptInFlightRef.current.delete(inflightKey);
            }
          }
        }

        setCachedResponse(prompt.system, prompt.user, cacheModel, fullText);
        ({ skills, themeVariant } = parseStep2Response(fullText));
      }

      const mergedSkills = mergeUniqueSkills([...baseSkills, ...skills]);
      const mergedThemeVariants = mergeUniqueStrings([...baseThemeVariants, ...themeVariant]);

      if (requestIdRef.current[field] === requestId) {
        dispatchStory({
          type: "setPartialDrafts",
          drafts: {
            skills: { options: mergedSkills },
            themeVariant: { options: mergedThemeVariants },
          },
        });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      console.error("Step 2 generation error:", error);
      if (requestIdRef.current[field] === requestId) {
        setGenerationError(error instanceof Error ? error.message : "Failed to generate content");
      }
    } finally {
      if (requestIdRef.current[field] === requestId) {
        setFieldGenerating(field, false);
        if (abortControllersRef.current[field] === controller) {
          delete abortControllersRef.current[field];
        }
      }
    }
  }, [story, dispatchStory, getPromptInFlightKey]);

  // Generate Step 3: main objective only
  const generateStep3 = useCallback(async (
    options?: { mode?: "replace" | "append"; keepObjectives?: Objective[]; forceFresh?: boolean },
  ) => {
    if (!story) return;

    const field: GenerationField = "step3";
    const controller = createAbortController(field);
    const requestId = nextRequestId(field);

    setFieldGenerating(field, true);
    setGenerationError(null);

    try {
      const tokens = loadAllTokens();
      const hasAnyToken = tokens["default"] || tokens.copilot || tokens.antigravity ||
        tokens.openrouter || tokens.gemini || tokens.groq || tokens.perplexity;

      if (!hasAnyToken) {
        setGenerationError("No AI provider connected");
        return;
      }

      const prompt = buildStep3Prompt(
        story.settings,
        story.settings.lockedSkills,
        SUGGESTION_COUNT,
      );

      const provider = tokens.activeProvider ||
        (tokens["default"]
          ? "default"
          : tokens.copilot
          ? "copilot"
          : tokens.antigravity
          ? "antigravity"
          : tokens.openrouter
          ? "openrouter"
          : tokens.gemini
          ? "gemini"
          : tokens.groq
          ? "groq"
          : tokens.perplexity
          ? "perplexity"
          : null);

      if (!provider) throw new Error("No active provider found");

      const activeToken = tokens[provider];
      if (!activeToken) throw new Error(`No token found for provider: ${provider}`);

      const selectedModel = getSelectedModel(provider);
      const cacheModel = getModelCacheKey(provider, selectedModel);

      const baseObjectives = options?.keepObjectives ?? [];

      const cached = options?.forceFresh
        ? null
        : getCachedResponse(prompt.system, prompt.user, cacheModel);

      let objectives: Objective[];
      const inflightKey = getPromptInFlightKey(cacheModel, prompt);

      if (cached) {
        objectives = parseStep3Response(cached);
      } else {
        const existingRequest = promptInFlightRef.current.get(inflightKey);
        let fullText: string;

        if (existingRequest) {
          fullText = await existingRequest;
        } else {
          const requestPromise = (async () => {
            const response = await fetch("/api/chat", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              signal: controller.signal,
              body: JSON.stringify({
                provider,
                model: selectedModel,
                messages: buildMessages(prompt),
                accessToken: activeToken.accessToken,
                max_tokens: prompt.maxTokens,
              }),
            });

            if (!response.ok) {
              let message = `AI request failed: ${response.statusText}`;
              try {
                const errorData = await response.json();
                message = errorData.error || message;
              } catch {
                // Ignore
              }
              throw new Error(message);
            }

            return await readLLMStream(response, (partialText) => {
              if (controller.signal.aborted) return;
              const { drafts } = parseIncrementalStoryDrafts(partialText);
              if (drafts.mainObjective?.options) {
                const mergedObjectives = mergeUniqueObjectives([
                  ...baseObjectives,
                  ...drafts.mainObjective.options,
                ]);
                dispatchStory({
                  type: "setPartialDrafts",
                  drafts: { mainObjective: { options: mergedObjectives } },
                });
              }
            });
          })();

          promptInFlightRef.current.set(inflightKey, requestPromise);
          try {
            fullText = await requestPromise;
          } finally {
            if (promptInFlightRef.current.get(inflightKey) === requestPromise) {
              promptInFlightRef.current.delete(inflightKey);
            }
          }
        }

        setCachedResponse(prompt.system, prompt.user, cacheModel, fullText);
        objectives = parseStep3Response(fullText);
      }

      const mergedObjectives = mergeUniqueObjectives([...baseObjectives, ...objectives]);

      if (requestIdRef.current[field] === requestId) {
        dispatchStory({
          type: "setPartialDrafts",
          drafts: {
            mainObjective: { options: mergedObjectives },
          },
        });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      console.error("Step 3 generation error:", error);
      if (requestIdRef.current[field] === requestId) {
        setGenerationError(error instanceof Error ? error.message : "Failed to generate content");
      }
    } finally {
      if (requestIdRef.current[field] === requestId) {
        setFieldGenerating(field, false);
        if (abortControllersRef.current[field] === controller) {
          delete abortControllersRef.current[field];
        }
      }
    }
  }, [story, dispatchStory, getPromptInFlightKey]);

  // Generate Step 4 field: on-demand generation for specific fields
  const generateFieldDraft = useCallback(
    async (
      field: "twist" | "intro" | "sideQuest" | "character" | "location" | "encounter",
      options?: { mode?: "replace" | "append"; forceFresh?: boolean },
    ) => {
      if (!story) return [];

      const generationField: GenerationField = field;
      const controller = createAbortController(generationField);
      const requestId = nextRequestId(generationField);

      setFieldGenerating(generationField, true);
      setGenerationError(null);

      try {
        const tokens = loadAllTokens();
        const hasAnyToken = tokens["default"] || tokens.copilot || tokens.antigravity ||
          tokens.openrouter || tokens.gemini || tokens.groq || tokens.perplexity;

        if (!hasAnyToken) {
          setGenerationError("No AI provider connected");
          return [];
        }

        if (options?.mode === "replace" && options.forceFresh) {
          resetStep4Conversation();
        }

        const conversationKey = buildStep4ConversationKey(story, field);
        const isNewConversation = step4ConversationRef.current.key !== conversationKey ||
          step4ConversationRef.current.field !== field;

        const prompt = isNewConversation
          ? buildStep4FieldPrompt(
            field,
            story.settings,
            story.settings.lockedSkills,
            story.storySeed.mainObjective,
            SUGGESTION_COUNT,
          )
          : buildStep4ContinuationPrompt(
            field,
            story.settings,
            story.settings.lockedSkills,
            story.storySeed.mainObjective,
            SUGGESTION_COUNT,
          );

        const provider = tokens.activeProvider ||
          (tokens["default"]
            ? "default"
            : tokens.copilot
            ? "copilot"
            : tokens.antigravity
            ? "antigravity"
            : tokens.openrouter
            ? "openrouter"
            : tokens.gemini
            ? "gemini"
            : tokens.groq
            ? "groq"
            : tokens.perplexity
            ? "perplexity"
            : null);

        if (!provider) throw new Error("No active provider found");

        const activeToken = tokens[provider];
        if (!activeToken) throw new Error(`No token found for provider: ${provider}`);

        const selectedModel = getSelectedModel(provider);
        const cacheModel = getModelCacheKey(provider, selectedModel);

        let messages: ChatMessage[];
        if (isNewConversation) {
          messages = buildMessages(prompt);
          step4ConversationRef.current = { key: conversationKey, field, messages: [...messages] };
        } else {
          messages = [
            ...step4ConversationRef.current.messages,
            { role: "user", content: prompt.user },
          ];
        }

        const canUseCache = isNewConversation && !options?.forceFresh;
        const cached = canUseCache
          ? getCachedResponse(prompt.system, prompt.user, cacheModel)
          : null;

        let items: Character[] | Location[] | SideQuest[] | Encounter[] | string[];

        if (cached) {
          items = parseStep4FieldResponse(cached, field);
          step4ConversationRef.current.messages = [
            ...messages,
            { role: "assistant", content: cached },
          ];
        } else {
          const response = await fetch("/api/chat", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            signal: controller.signal,
            body: JSON.stringify({
              provider,
              model: selectedModel,
              messages,
              accessToken: activeToken.accessToken,
              max_tokens: prompt.maxTokens,
            }),
          });

          if (!response.ok) {
            let message = `AI request failed: ${response.statusText}`;
            try {
              const errorData = await response.json();
              message = errorData.error || message;
            } catch {
              // Ignore
            }
            throw new Error(message);
          }

          const fullText = await readLLMStream(response, (partialText) => {
            if (controller.signal.aborted) return;
            const { drafts } = parseIncrementalStoryDrafts(partialText);
            if (Object.keys(drafts).length > 0) {
              dispatchStory({ type: "setPartialDrafts", drafts });
            }
          });

          if (canUseCache) {
            setCachedResponse(prompt.system, prompt.user, cacheModel, fullText);
          }

          items = parseStep4FieldResponse(fullText, field);
          step4ConversationRef.current.messages = [
            ...messages,
            { role: "assistant", content: fullText },
          ];
        }

        if (requestIdRef.current[generationField] === requestId) {
          const draftField = field === "twist"
            ? "storyTwist"
            : field === "intro"
            ? "storyIntro"
            : field === "sideQuest"
            ? "sideQuests"
            : field === "character"
            ? "characters"
            : field === "location"
            ? "locations"
            : "encounters";

          const mergedDrafts = options?.mode === "append"
            ? appendFieldDrafts(story.drafts, field, items)
            : ({ [draftField]: { options: items } } as Partial<StorySeedDrafts>);

          dispatchStory({
            type: "setPartialDrafts",
            drafts: mergedDrafts,
          });
        }
        return items;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return [];
        }
        console.error(`Step 4 ${field} generation error:`, error);
        if (requestIdRef.current[generationField] === requestId) {
          setGenerationError(error instanceof Error ? error.message : "Failed to generate content");
        }
        return [];
      } finally {
        if (requestIdRef.current[generationField] === requestId) {
          setFieldGenerating(generationField, false);
          if (abortControllersRef.current[generationField] === controller) {
            delete abortControllersRef.current[generationField];
          }
        }
      }
    },
    [story, dispatchStory, resetStep4Conversation],
  );

  const generateStep4Bulk = useCallback(async (options?: { forceFresh?: boolean }) => {
    if (!story) return;

    const field: GenerationField = "content";
    const controller = createAbortController(field);
    const requestId = nextRequestId(field);

    const trackedFields: Array<
      "storyName" | "intro" | "twist" | "sideQuest" | "character" | "location" | "encounter"
    > = ["storyName", "intro", "twist", "sideQuest", "character", "location", "encounter"];

    setFieldGenerating(field, true);
    trackedFields.forEach((trackedField) => setFieldGenerating(trackedField, true));
    setGenerationError(null);

    const pickStep4Drafts = (drafts: Partial<StorySeedDrafts>): Partial<StorySeedDrafts> => {
      const next: Partial<StorySeedDrafts> = {};
      if (drafts.storyName) next.storyName = drafts.storyName;
      if (drafts.storyIntro) next.storyIntro = drafts.storyIntro;
      if (drafts.storyTwist) next.storyTwist = drafts.storyTwist;
      if (drafts.sideQuests) next.sideQuests = drafts.sideQuests;
      if (drafts.characters) next.characters = drafts.characters;
      if (drafts.locations) next.locations = drafts.locations;
      if (drafts.encounters) next.encounters = drafts.encounters;
      return next;
    };

    try {
      const tokens = loadAllTokens();
      const hasAnyToken = tokens["default"] || tokens.copilot || tokens.antigravity ||
        tokens.openrouter || tokens.gemini || tokens.groq || tokens.perplexity;

      if (!hasAnyToken) {
        setGenerationError("No AI provider connected");
        return;
      }

      const lockStateForPrompt = {
        ...story.lockState,
        mainObjective: true,
      };

      const prompt = buildGenerationPrompt(
        story.storySeed,
        lockStateForPrompt,
        story.settings,
        SUGGESTION_COUNT,
      );

      const provider = tokens.activeProvider ||
        (tokens["default"]
          ? "default"
          : tokens.copilot
          ? "copilot"
          : tokens.antigravity
          ? "antigravity"
          : tokens.openrouter
          ? "openrouter"
          : tokens.gemini
          ? "gemini"
          : tokens.groq
          ? "groq"
          : tokens.perplexity
          ? "perplexity"
          : null);

      if (!provider) throw new Error("No active provider found");

      const activeToken = tokens[provider];
      if (!activeToken) throw new Error(`No token found for provider: ${provider}`);

      const selectedModel = getSelectedModel(provider);
      const cacheModel = getModelCacheKey(provider, selectedModel);

      const cached = options?.forceFresh
        ? null
        : getCachedResponse(prompt.system, prompt.user, cacheModel);
      const inflightKey = getPromptInFlightKey(cacheModel, prompt);

      if (cached) {
        const generatedDrafts = parseAIResponse(cached, story.settings.slotCounts);
        if (requestIdRef.current[field] === requestId) {
          dispatchStory({ type: "setPartialDrafts", drafts: pickStep4Drafts(generatedDrafts) });
        }
        return;
      }

      const existingRequest = promptInFlightRef.current.get(inflightKey);
      let fullText: string;

      if (existingRequest) {
        fullText = await existingRequest;
      } else {
        const requestPromise = (async () => {
          const response = await fetch("/api/chat", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            signal: controller.signal,
            body: JSON.stringify({
              provider,
              model: selectedModel,
              messages: buildMessages(prompt),
              accessToken: activeToken.accessToken,
              max_tokens: prompt.maxTokens,
            }),
          });

          if (!response.ok) {
            let message = `AI request failed: ${response.statusText}`;
            try {
              const errorData = await response.json();
              message = errorData.error || message;
            } catch {
              // Ignore
            }
            throw new Error(message);
          }

          return await readLLMStream(response, (partialText) => {
            if (controller.signal.aborted) return;
            const { drafts } = parseIncrementalStoryDrafts(partialText);
            const filtered = pickStep4Drafts(drafts);
            if (Object.keys(filtered).length === 0) return;

            dispatchStory({ type: "setPartialDrafts", drafts: filtered });

            if (filtered.storyName) setFieldGenerating("storyName", false);
            if (filtered.storyIntro) setFieldGenerating("intro", false);
            if (filtered.storyTwist) setFieldGenerating("twist", false);
            if (filtered.sideQuests) setFieldGenerating("sideQuest", false);
            if (filtered.characters) setFieldGenerating("character", false);
            if (filtered.locations) setFieldGenerating("location", false);
            if (filtered.encounters) setFieldGenerating("encounter", false);
          });
        })();

        promptInFlightRef.current.set(inflightKey, requestPromise);
        try {
          fullText = await requestPromise;
        } finally {
          if (promptInFlightRef.current.get(inflightKey) === requestPromise) {
            promptInFlightRef.current.delete(inflightKey);
          }
        }
      }

      setCachedResponse(prompt.system, prompt.user, cacheModel, fullText);

      if (requestIdRef.current[field] === requestId) {
        try {
          const generatedDrafts = parseAIResponse(fullText, story.settings.slotCounts);
          dispatchStory({ type: "setPartialDrafts", drafts: pickStep4Drafts(generatedDrafts) });
        } catch (parseError) {
          console.warn(
            "Step 4 bulk final parse failed, keeping streamed partial drafts:",
            parseError,
          );
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      console.error("Step 4 bulk generation error:", error);
      if (requestIdRef.current[field] === requestId) {
        setGenerationError(error instanceof Error ? error.message : "Failed to generate content");
      }
    } finally {
      if (requestIdRef.current[field] === requestId) {
        setFieldGenerating(field, false);
        trackedFields.forEach((trackedField) => setFieldGenerating(trackedField, false));
        if (abortControllersRef.current[field] === controller) {
          delete abortControllersRef.current[field];
        }
      }
    }
  }, [
    story,
    createAbortController,
    dispatchStory,
    nextRequestId,
    setFieldGenerating,
    getPromptInFlightKey,
  ]);

  const preloadStep2 = useCallback(async () => {
    if (!story) return;
    const prompt = buildStep2Prompt(story.settings, STEP2_SKILL_COUNT, STEP2_THEME_VARIANT_COUNT);
    await preloadPromptToCache(prompt);
  }, [story, preloadPromptToCache]);

  const preloadStep3 = useCallback(async (skills: Skill[], themeVariant: string) => {
    if (!story) return;

    const settingsForPrompt = {
      ...story.settings,
      themeVariant,
    };

    const prompt = buildStep3Prompt(settingsForPrompt, skills, SUGGESTION_COUNT);
    await preloadPromptToCache(prompt);
  }, [story, preloadPromptToCache]);

  const preloadStep4Bulk = useCallback(async (objective: Objective) => {
    if (!story || !objective.description.trim()) return;

    const storySeedForPrompt = {
      ...story.storySeed,
      mainObjective: objective,
    };
    const lockStateForPrompt = {
      ...story.lockState,
      mainObjective: true,
    };

    const prompt = buildGenerationPrompt(
      storySeedForPrompt,
      lockStateForPrompt,
      story.settings,
      SUGGESTION_COUNT,
    );

    await preloadPromptToCache(prompt);
  }, [story, preloadPromptToCache]);

  const generateSkillUses = useCallback(
    async (
      itemType: "objective" | "sideQuest" | "character" | "location" | "encounter",
      itemIndex?: number,
      options?: { forceFresh?: boolean; targetDescription?: string },
    ) => {
      if (!story) return [];
      if (story.settings.lockedSkills.length === 0) {
        setGenerationError("No locked skills available");
        return [];
      }

      setGenerationError(null);
      const controller = new AbortController();

      try {
        const tokens = loadAllTokens();
        const hasAnyToken = tokens["default"] || tokens.copilot || tokens.antigravity ||
          tokens.openrouter || tokens.gemini || tokens.groq || tokens.perplexity;

        if (!hasAnyToken) {
          setGenerationError("No AI provider connected");
          return [];
        }

        let targetDescription = options?.targetDescription?.trim() || "";
        if (!targetDescription && itemType === "objective") {
          targetDescription = story.storySeed.mainObjective.description;
        } else if (!targetDescription && itemType === "sideQuest") {
          const quest = story.storySeed.sideQuests[itemIndex ?? 0];
          targetDescription = quest?.description || "";
        } else if (!targetDescription && itemType === "character") {
          const character = story.storySeed.characters[itemIndex ?? 0];
          targetDescription = character ? `${character.name} (${character.role})` : "";
        } else if (!targetDescription && itemType === "location") {
          const location = story.storySeed.locations[itemIndex ?? 0];
          targetDescription = location ? `${location.name} - ${location.description}` : "";
        } else if (!targetDescription && itemType === "encounter") {
          const encounter = story.storySeed.encounters[itemIndex ?? 0];
          targetDescription = encounter?.description || "";
        }

        if (!targetDescription) {
          setGenerationError("No target item found to add skill uses");
          return [];
        }

        const prompt = buildSkillUsesPrompt(
          story.settings,
          story.settings.lockedSkills,
          itemType,
          targetDescription,
        );

        const provider = tokens.activeProvider ||
          (tokens["default"]
            ? "default"
            : tokens.copilot
            ? "copilot"
            : tokens.antigravity
            ? "antigravity"
            : tokens.openrouter
            ? "openrouter"
            : tokens.gemini
            ? "gemini"
            : tokens.groq
            ? "groq"
            : tokens.perplexity
            ? "perplexity"
            : null);

        if (!provider) throw new Error("No active provider found");

        const activeToken = tokens[provider];
        if (!activeToken) throw new Error(`No token found for provider: ${provider}`);

        const selectedModel = getSelectedModel(provider);
        const cacheModel = getModelCacheKey(provider, selectedModel);

        const cached = options?.forceFresh
          ? null
          : getCachedResponse(prompt.system, prompt.user, cacheModel);
        if (cached) {
          return parseSkillUsesResponse(cached);
        }

        const response = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          signal: controller.signal,
          body: JSON.stringify({
            provider,
            model: selectedModel,
            messages: buildMessages(prompt),
            accessToken: activeToken.accessToken,
            max_tokens: prompt.maxTokens,
          }),
        });

        if (!response.ok) {
          let message = `AI request failed: ${response.statusText}`;
          try {
            const errorData = await response.json();
            message = errorData.error || message;
          } catch {
            // Ignore
          }
          throw new Error(message);
        }

        const fullText = await readLLMStream(response, () => {});
        setCachedResponse(prompt.system, prompt.user, cacheModel, fullText);
        const skillUses = parseSkillUsesResponse(fullText);

        return skillUses;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return [];
        }
        console.error("Skill uses generation error:", error);
        setGenerationError(error instanceof Error ? error.message : "Failed to generate content");
        return [];
      }
    },
    [story],
  );

  return {
    story,
    generatingFields,
    generationError,
    generateContent,
    generateStoryNames,
    clearDrafts,
    updateDraftOptions,
    generateStep2,
    generateStep3,
    generateFieldDraft,
    generateStep4Bulk,
    preloadStep2,
    preloadStep3,
    preloadStep4Bulk,
    selectSuggestion,
    removeSuggestion,
    toggleLock,
    lockAll,
    unlockAll,
    updateSettings,
    updateStoryName,
    updateStoryIntro,
    updateSlotCount,
    updateSideQuest,
    updateCharacter,
    updateLocation,
    updateEncounter,
    updateSkill,
    updateMainObjective,
    updateStoryTwist,
    setStep,
    lockInStep2,
    lockInStep3,
    generateSkillUses,
    loadTestData,
  };
}
